import { useContext, useState } from "react";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import useAppNavigate from "../../../components/router/use-app-navigate";
import AuthContext from "../../../components/auth-context";
import Form from "../../../components/inputs/form";
import Textbox from "../../../components/inputs/textbox";
import MessageBox from "../../../components/dashboard/message-box";
import Auth, { AuthProps } from "../../../api/auth";
import ChangeUserNameForm from "../../../types/change-username-form";
import ChangeCellPhoneApi from "../../../api/change-userName";

function ChangeUserName() {
  const { register, handleSubmit, getValues } = useForm<ChangeUserNameForm>();
  const navigate = useAppNavigate();
  const { t } = useTranslation(undefined, { keyPrefix: "ChangeUserName" });
  const context = useContext(AuthContext);

  const [sendingCode, setSendingCode] = useState(false);
  const [isCodeSent, setIsCodeSent] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [successMessage, setSuccessMessage] = useState("");

  const onSubmit = async (data: ChangeUserNameForm): Promise<string> => {
    const { CellPhone, Code } = data;

    if (!isCodeSent) {
      setErrorMessage(t("Error.CodeNotSent"));
      return "Code not sent";
    }

    try {
      setErrorMessage("");
      setSuccessMessage("");

      const api = new ChangeCellPhoneApi(context);
      const response = await api.put({
        CellPhone,
        Code,
      });

      if (response?.errorMessages) {
        setErrorMessage(response.errorMessages);
        return "error";
      }

      setSuccessMessage(t("Success.OperationCompleted"));
      const loginRes = await Auth.login({
        username: CellPhone.toString(),
        password: Code?.toString()!,
      });
      console.log("loginRes.LoginId", loginRes.LoginId);
      return "success";
    } catch (err) {
      console.error("Error submitting change sim card:", err);

      if (err instanceof Error) {
        setErrorMessage(err.message);
        return "error";
      } else if (typeof err === "object" && err !== null && "response" in err) {
        const e = err as any;
        const serverError =
          e.response?.data?.errorMessage ||
          e.response?.data?.message ||
          t("Error.ServerError");
        setErrorMessage(serverError);
        return "server error";
      } else {
        setErrorMessage(t("Error.Generic"));
        return "unexpected error";
      }
    }
  };

  const handleSendCode = async () => {
    const { CellPhone } = getValues();

    if (!CellPhone) {
      setErrorMessage(t("Error.PhoneNumberRequired"));
      return;
    }

    try {
      setSendingCode(true);
      setErrorMessage("");
      setSuccessMessage("");

      const api = new ChangeCellPhoneApi(context);
      const response = await api.post({ CellPhone });

      if (response?.errorMessage) {
        setErrorMessage(response.errorMessage);
        setIsCodeSent(false);
        return;
      }

      setIsCodeSent(true);
      setSuccessMessage(t("Success.CodeSent"));
    } catch (err) {
      console.error("Error sending Code:", err);

      if (err instanceof Error) {
        setErrorMessage(err.message);
      } else if (typeof err === "object" && err !== null && "response" in err) {
        const e = err as any;
        const msg =
          e.response?.data?.errorMessage ||
          e.response?.data?.message ||
          t("Error.CodeSendFailed");
        setErrorMessage(msg);
      } else {
        setErrorMessage(t("Error.CodeSendFailed"));
      }

      setIsCodeSent(false);
    } finally {
      setSendingCode(false);
    }
  };

  return (
    <div className="flex justify-center items-center">
      <Form submit={onSubmit} handleSubmit={handleSubmit}>
        <h3 className="lead-title pt-2">{t("Title")}</h3>

        {errorMessage && <MessageBox message={errorMessage} type="error" />}
        {successMessage && (
          <MessageBox message={successMessage} type="success" />
        )}

        <Textbox {...register("CellPhone")} label={t("PhoneNumber")} />

        <div className="relative w-full mt-2">
          <Textbox {...register("Code")} label={t("Code")} />
          <button
            type="button"
            onClick={handleSendCode}
            disabled={sendingCode}
            className="absolute left-0 top-1/2 -translate-y-1/2 bg-orange-500 text-white rounded-lg px-3 py-2 text-sm hover:bg-orange-600 disabled:bg-gray-400"
          >
            {sendingCode ? t("Sending") : t("SendCode")}
          </button>
        </div>

        <div
          dir="rtl"
          className="rule-section flex items-center gap-2 relative mt-8"
        >
          <button
            type="submit"
            className="signup-btn p-5 flex bottom-0 items-center gap-2 absolute left-10"
          >
            {t("Submit")}
            <span className="sepanta-icon-left-long-arrow"></span>
          </button>
        </div>
      </Form>
    </div>
  );
}

export default ChangeUserName;
